// CRITICAL: Sentry must be imported FIRST before any other code
import { BRAND_SITE_HOST, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import "./sentry.client.config";

import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./i18n/config";
import { installSiteDebugGlobals } from "./lib/siteDebug";
import { initAnalyticsErrorHandler } from "./lib/analyticsErrorHandler";
import { initAnalyticsLoader } from "./lib/loadAnalytics";
import { setupMetaPixelMonitor } from "./lib/metaPixelMonitor";
import {
  getUserRegionForTracking,
  initPersonPropertiesPostHog,
  registerSuperPropertiesPostHog,
  startPostHogGeoRefresh,
  trackErrorPostHog,
  trackWebVitalsPostHog,
} from "./lib/posthogAnalytics";

// ── Canonical host guard (apex, sin www) ──────────────────────────────────────
// El origen canónico es el apex. La infra 308-redirige www→apex para /api/* pero
// NO para las rutas de página, así que el SPA de admin podía cargar en www y
// entonces cada fetch autenticado a /api/admin/* pegaba al 308 cross-origin — y
// los navegadores DESCARTAN la cabecera Authorization en un redirect cross-origin
// → "Missing Authorization header" (401) en todo el panel de admin.
// Forzamos apex ANTES de inicializar Clerk/analytics/fetch para que las llamadas
// queden same-origin y conserven el token.
const isRedirectingToApex =
  typeof window !== "undefined" &&
  window.location.hostname.startsWith("www.");

if (isRedirectingToApex) {
  const apexHost = window.location.hostname.replace(/^www\./, "");
  const target = `${window.location.protocol}//${apexHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(target);
  // NO seguimos: `window.location.replace` es asíncrono y el resto de este
  // módulo seguiría ejecutándose. Si el SPA llegara a MONTAR en www, sus fetch
  // a /api/admin/* pegan al hop www→apex que DESCARTA la cabecera Authorization
  // (→ 401 "Missing session token"). Al cortar el render acá (ver guard al final
  // del archivo) garantizamos que NINGUNA request autenticada salga jamás desde
  // un host no-canónico, sin depender de la config de infra/worker.
}

installSiteDebugGlobals();

// Meta Pixel runtime monitor — captures every fbq event into a circular buffer
// reachable via window.__metaPixelLog__ / window.__metaPixelStatus__() for
// instant diagnostic from DevTools (no Meta Events Manager round-trip).
setupMetaPixelMonitor({ pixelId: "1018103057361682" });

// Initialize analytics error handler to silence blocked analytics errors
initAnalyticsErrorHandler();

// Initialize lazy loading of third-party analytics (GA, Meta Pixel, Metricool, Tally)
// These load after user interaction or 4s idle for better FCP/LCP
initAnalyticsLoader();

// PostHog init is deferred until after first paint (requestIdleCallback) so
// posthog-js does not compete with React on the critical path.

// Support both NEXT_PUBLIC_ (Next.js convention) and VITE_ (Vite convention) env vars
const posthogKey = 
  import.meta.env.NEXT_PUBLIC_POSTHOG_KEY || 
  import.meta.env.VITE_POSTHOG_KEY || 
  "";
const posthogHost =
  import.meta.env.NEXT_PUBLIC_POSTHOG_HOST ||
  import.meta.env.VITE_POSTHOG_HOST ||
  (import.meta.env.DEV ? "/ingest" : "https://us.i.posthog.com");

const posthogEventQueue: Array<[string, Record<string, unknown> | undefined]> = [];

function flushPostHogEventQueue(): void {
  if (typeof window === "undefined") return;
  const posthog = (window as { posthog?: { capture?: (n: string, p?: Record<string, unknown>) => void; __loaded?: boolean } }).posthog;
  if (!posthog?.capture || !posthog.__loaded) return;
  while (posthogEventQueue.length > 0) {
    const [eventName, properties] = posthogEventQueue.shift()!;
    try {
      posthog.capture(eventName, properties);
    } catch {
      /* ignore */
    }
  }
}

function schedulePostHogInit(): void {
  const run = () => {
    void import("posthog-js").then(({ default: posthog }) => {
      initPostHog(posthog);
    }).catch(() => {
      if (import.meta.env.DEV) {
        console.debug("PostHog bloqueado por el usuario o error de inicialización");
      }
    });
  };

  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    window.setTimeout(run, 1);
  }
}

function initPostHog(posthog: typeof import("posthog-js").default): void {
  try {
    if (typeof window !== "undefined") {
      (window as { posthog?: typeof posthog }).posthog = posthog;
    }
    
    posthog.init(posthogKey, {
      api_host: posthogHost,
      // Filter Script error (cross-origin third-party like Stripe/Mux/IG/GA without proper CORS)
      before_send: (payload) => {
        if (payload.event === '$exception' || payload.event === 'exception') {
          const msg = String(payload.properties?.$exception_message || payload.properties?.message || '');
          if (msg === 'Script error.' || msg.includes('Script error')) {
            return null;
          }
        }
        return payload;
      },
      // Session Replay: explícito; el dashboard del proyecto también debe tener «Record user sessions» activo.
      disable_session_recording: false,
      // Person profiles: 'always' creates profiles for all visitors (including anonymous)
      // This is required to see session replays and user properties in PostHog for any user
      person_profiles: 'always',
      // Alineado con la plantilla de instalación PostHog (project defaults)
      defaults: "2026-01-30",
      // SPA: we capture pageviews on route changes via <PostHogPageview />
      capture_pageview: false,
      capture_pageleave: true,
      // Session recording - enabled for ALL templates (BaseTemplate, BioLinkTemplate, ArgentinaBioTemplate)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      session_recording: ({
        enabled: true,
        record_cross_origin_iframes: false, // Disable cross-origin iframe recording
        capture_console_logs: true, // Capture console logs for debugging
        // Network request capture - enabled as per PostHog project settings
        // Note: recordHeaders and recordBody are configured in PostHog project settings
        // The dashboard settings you activated will control headers/body capture
        capture_network_requests: true, // Enable network request capture
        // Custom function to mask sensitive network data
        // This ensures sensitive headers and payloads are redacted even if dashboard settings change
        maskCapturedNetworkRequestFn: (request: any) => {
          if (!request) return null;

          // Skip analytics/telemetry + PostHog ingestion — noise in replays and can surface
          // false "broken" payloads (e.g. CSP /report/ 400 invalid_payload when something
          // POSTs application/json instead of application/csp-report|reports+json).
          const url = String(request.url || request.name || "");
          const status = Number(request.status ?? request.responseStatus ?? 0);
          const isPosthogHost = /(?:^|\.)(?:i\.)?posthog\.com(?::\d+)?(?:\/|$)/i.test(
            (() => {
              try {
                return new URL(url, typeof location !== "undefined" ? location.origin : "https://juaperrone.com").host;
              } catch {
                return url;
              }
            })(),
          );
          // PostHog ingest hosts + CSP report path (any host) — never useful in replay
          if (
            isPosthogHost ||
            /axiom\.co|inferred\.litix\.io/i.test(url) ||
            /\/report\/?\?.*token=/i.test(url) ||
            /\/report\/?$/i.test(url.split("?")[0] || "")
          ) {
            return null;
          }

          // Drop failed CSP report responses even if host was proxied/rewritten
          const respBody =
            typeof request.responseBody === "string" ? request.responseBody : "";
          if (
            status === 400 &&
            (respBody.includes("Failed to submit CSP report") ||
              respBody.includes("invalid_csp_payload") ||
              respBody.includes('"invalid_payload"'))
          ) {
            return null;
          }
          
          // List of sensitive headers to mask
          const sensitiveHeaders = [
            'authorization',
            'x-api-key',
            'api-key',
            'x-auth-token',
            'cookie',
            'set-cookie',
            'x-csrf-token',
            'csrf-token',
            'x-session-id',
            'session-id',
            'x-access-token',
            'access-token',
            'x-refresh-token',
            'refresh-token',
            'stripe-signature',
            'x-stripe-signature',
          ];
          
          // Mask sensitive headers
          if (request.requestHeaders) {
            sensitiveHeaders.forEach(header => {
              const lowerHeader = header.toLowerCase();
              if (request.requestHeaders.has(lowerHeader)) {
                request.requestHeaders.set(lowerHeader, '[REDACTED]');
              }
            });
          }
          
          if (request.responseHeaders) {
            sensitiveHeaders.forEach(header => {
              const lowerHeader = header.toLowerCase();
              if (request.responseHeaders.has(lowerHeader)) {
                request.responseHeaders.set(lowerHeader, '[REDACTED]');
              }
            });
          }
          
          // Mask sensitive data in request body
          if (request.requestBody && typeof request.requestBody === 'string') {
            try {
              const body = JSON.parse(request.requestBody);
              const sensitiveFields = [
                'password', 'password_confirmation', 'current_password', 'new_password',
                'token', 'api_key', 'apiKey', 'access_token', 'refresh_token',
                'secret', 'secret_key', 'private_key', 'credit_card', 'card_number',
                'cvv', 'cvc', 'card_cvv', 'card_cvc', 'ssn', 'social_security_number',
                'stripe_token', 'stripeToken',
              ];
              
              sensitiveFields.forEach(field => {
                if (body[field]) {
                  body[field] = '[REDACTED]';
                }
              });
              
              request.requestBody = JSON.stringify(body);
            } catch (e) {
              // If body is not JSON, check for sensitive patterns
              const sensitivePatterns = [
                /"password"\s*:\s*"[^"]*"/gi,
                /"token"\s*:\s*"[^"]*"/gi,
                /"api[_-]?key"\s*:\s*"[^"]*"/gi,
                /"secret"\s*:\s*"[^"]*"/gi,
              ];
              
              sensitivePatterns.forEach(pattern => {
                request.requestBody = request.requestBody.replace(pattern, (match: string) => {
                  return match.replace(/:\s*"[^"]*"/, ': "[REDACTED]"');
                });
              });
            }
          }
          
          // Mask sensitive data in response body
          if (request.responseBody && typeof request.responseBody === 'string') {
            try {
              const body = JSON.parse(request.responseBody);
              const sensitiveFields = ['token', 'access_token', 'refresh_token', 'api_key', 'secret'];
              
              sensitiveFields.forEach(field => {
                if (body[field]) {
                  body[field] = '[REDACTED]';
                }
              });
              
              request.responseBody = JSON.stringify(body);
            } catch (e) {
              // If body is not JSON, apply pattern matching
              const sensitivePatterns = [
                /"token"\s*:\s*"[^"]*"/gi,
                /"api[_-]?key"\s*:\s*"[^"]*"/gi,
              ];
              
              sensitivePatterns.forEach(pattern => {
                request.responseBody = request.responseBody.replace(pattern, (match: string) => {
                  return match.replace(/:\s*"[^"]*"/, ': "[REDACTED]"');
                });
              });
            }
          }
          
          return request;
        },
        sample_rate: (() => {
          const raw = import.meta.env.VITE_POSTHOG_REPLAY_SAMPLE_RATE;
          if (raw != null && String(raw).trim() !== "") {
            const n = Number(raw);
            if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
          }
          // Dominio producción: grabar sesiones completas para funnels dual/Mux → membresía
          if (
            typeof window !== "undefined"
            && /(?:^|\.)juaperrone\.com$/i.test(
              window.location.hostname.replace(/^www\./i, ""),
            )
          ) {
            return 1;
          }
          return 0.15;
        })(),
        minimum_duration: 0, // Record all sessions regardless of duration
        // Mask sensitive data
        mask_all_inputs: true, // Mask all input fields for privacy
        mask_text_selector: '[data-ph-mask]', // Custom selector for masking
        // Performance optimizations
        record_canvas_fps: 2, // Low FPS for canvas if enabled
      } as any),
      // Autocapture - enabled to track all events on all templates
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      autocapture: ({
        css_selector_allowlist: ["[data-attr]", "button", "a", "[data-ph-capture]"], // Capture buttons, links, and elements with data attributes
        url_allowlist: [], // Capture on all URLs (empty = all URLs)
        dom_event_allowlist: ["click", "change", "submit", "input" as any], // Capture clicks, form changes, and input events
        capture_copied_text: true, // Capture copied text
        // Capture all interactive elements
        capture_rage_clicks: true, // Capture rage clicks
        capture_dead_clicks: true, // Capture dead clicks
      } as any),
      // Enable feature flags
      bootstrap: {},
      loaded: (posthogInstance) => {
        // Ensure PostHog is available globally
        if (typeof window !== "undefined") {
          (window as any).posthog = posthogInstance;
        }
        
        // Debug mode in development
        if (import.meta.env.DEV) {
          posthogInstance.debug(true);
        }

        // Register super properties — auto-attached to every future event
        registerSuperPropertiesPostHog();

        // Async IPLocate privacy enrichment (Worker inject may be cf_only on cache miss)
        startPostHogGeoRefresh();

        // Enrich person profile with custom properties + session count
        initPersonPropertiesPostHog();

        // Session Replay: obligatorio para todos (anónimos + Clerk). Override sampling/linked_flag.
        const kickRecording = () => {
          try {
            posthogInstance.startSessionRecording?.({
              sampling: true,
              linked_flag: true,
              url_trigger: true,
              event_trigger: true,
            });
          } catch {
            try {
              posthogInstance.startSessionRecording?.(true);
            } catch {
              /* ignore */
            }
          }
        };
        kickRecording();
        // Remote config del recorder puede llegar después de `loaded`.
        [1500, 4000, 10000].forEach((ms) => window.setTimeout(kickRecording, ms));

        // Observe Core Web Vitals (LCP, FCP, CLS, TTFB, INP)
        trackWebVitalsPostHog();

        // Global error tracking — wire up unhandled JS errors and promise rejections
        window.addEventListener("error", (event) => {
          const error = event.error instanceof Error
            ? event.error
            : new Error(event.message || "Unknown error");
          trackErrorPostHog(error, {
            error_type: "uncaught_exception",
            source: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          });
        });

        window.addEventListener("unhandledrejection", (event) => {
          const error = event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason ?? "Unhandled rejection"));
          trackErrorPostHog(error, { error_type: "unhandled_rejection" });
        });
        
        // Web vitals + error handlers after SDK ready
        flushPostHogEventQueue();
        
        // Send initialization event
        try {
          posthogInstance.capture('posthog_initialized', {
            timestamp: new Date().toISOString(),
            sdk_version: '1.335.0',
            autocapture_enabled: (posthogInstance as any).config?.autocapture?.enabled || false,
            session_recording_enabled:
              posthogInstance.sessionRecordingStarted?.() ||
              !(posthogInstance as any).config?.disable_session_recording,
            environment: import.meta.env.MODE || 'production',
          });
        } catch (error) {
          // silenced
        }
      },
      // Privacy settings
      respect_dnt: false, // Replay obligatorio para todos los visitantes
      opt_out_capturing_by_default: false,
      opt_out_persistence_by_default: false,
      // Performance settings
      request_batching: true,
      // Cross-domain tracking — true para que www.juaperrone.com y juaperrone.com
      // compartan misma session (cookie en .juaperrone.com). Alineado con
      // app_urls del proyecto PostHog que incluye ambos hosts.
      cross_subdomain_cookie: true,
      secure_cookie: true,
      // Auto-capture JS exceptions tied to session replay — matches project
      // setting `autocapture_exceptions_opt_in: true`. Errors aparecen en
      // /error-tracking + linkeados al replay del frame que los disparó.
      capture_exceptions: true,
      // Persistence
      persistence: "localStorage+cookie",
      persistence_name: `ph_${BRAND_SOURCE_KEY}`,
      // Advanced settings
      property_blacklist: [], // Properties to exclude
      sanitize_properties: null, // Function to sanitize properties
      xhr_headers: {}, // Custom headers for requests
      // Error handling
      on_xhr_error: (failedRequest) => {
        if (import.meta.env.DEV) {
        }
      },
    });

    if (typeof window !== "undefined") {
      window.addEventListener('error', (event) => {
        const msg = event.message || '';
        if (msg === 'Script error.' || msg.includes('Script error')) {
          event.stopImmediatePropagation?.();
          if (import.meta.env.DEV) {
            console.debug('[filtered] Script error from cross-origin script ignored for analytics');
          }
        }
      }, true);
    }
  } catch {
    if (import.meta.env.DEV) {
      console.debug("PostHog bloqueado por el usuario o error de inicialización");
    }
  }
}

if (typeof posthogKey === "string" && posthogKey.length > 0) {
  schedulePostHogInit();
} else {
  // Log when PostHog is not configured
  if (import.meta.env.DEV) {
    console.warn("PostHog not configured - NEXT_PUBLIC_POSTHOG_KEY or VITE_POSTHOG_KEY not found, continuing without analytics");
  }
  
  // Ensure window.posthog is undefined when not configured
  if (typeof window !== "undefined") {
    (window as any).posthog = undefined;
  }
}

// Always make safe capture function available, even if PostHog is not configured
if (typeof window !== "undefined") {
  // Safe PostHog capture helper - checks if PostHog is ready before capturing
  (window as { posthogSafeCapture?: (eventName: string, properties?: Record<string, unknown>) => boolean }).posthogSafeCapture = (eventName: string, properties?: Record<string, unknown>) => {
    if (typeof window === 'undefined') {
      return false;
    }
    
    const posthog = (window as { posthog?: { capture?: (n: string, p?: Record<string, unknown>) => void; __loaded?: boolean } }).posthog;
    if (posthog && typeof posthog.capture === 'function' && posthog.__loaded) {
      try {
        posthog.capture(eventName, properties);
        return true;
      } catch {
        return false;
      }
    }

    posthogEventQueue.push([eventName, properties]);
    return false;
  };

  // Helper to check if PostHog is ready
  (window as any).isPostHogReady = () => {
    if (typeof window === 'undefined') return false;
    const posthog = (window as any).posthog;
    return !!(posthog && typeof posthog.capture === 'function' && posthog.__loaded);
  };
}

// Helper functions for region and country testing (no copy/paste needed!)
if (typeof window !== "undefined") {
  (window as any).setRegion = (region: "US" | "SPAIN" | "LATAM" | "OTHER") => {
    // user_region_manual = override manual (única key que useRegion persiste)
    localStorage.setItem("user_region_manual", region);
    localStorage.removeItem("test_country"); // Clear country test
    window.location.reload();
  };

  (window as any).testCountry = (countryCode: string) => {
    localStorage.setItem("user_region_manual", "LATAM");
    localStorage.setItem("test_country", countryCode);
    // Simulate browser language
    Object.defineProperty(navigator, "language", {
      writable: true,
      value: `es-${countryCode}`,
    });
    window.location.reload();
  };

  // Test Cloudflare Enterprise geolocation (PRIMARY METHOD)
  (window as any).testCloudflareGeo = async () => {
    if (import.meta.env.DEV) {
      console.log(`🚀 Testing Cloudflare Enterprise geolocation (PRIMARY for ${BRAND_SITE_HOST})...`);
      console.log("   Cloudflare marked as unavailable:", !!(window as any).cloudflareUnavailable);
    }

    console.log("📍 Template configuration is injected by Cloudflare Worker");
    console.log("🔧 Check window.__TEMPLATE_CONFIG__ for current configuration");
    if ((window as any).__TEMPLATE_CONFIG__) {
      console.log("Current config:", (window as any).__TEMPLATE_CONFIG__);
    }
  };

  // Check Cloudflare status
  (window as any).checkCloudflareStatus = async () => {
    try {
      const response = await fetch("/api/cloudflare-status");
      const status = await response.json();
      if (import.meta.env.DEV) {
        console.log("   - Behind Cloudflare:", status.cloudflare.present ? "✅ YES" : "❌ NO");
        console.log("   - Geolocation Headers:", status.geolocation.hasCountry ? "✅ YES" : "❌ NO");
        console.log("   - True-Client-IP:", status.cloudflare.hasTrueClientIp ? "✅ YES" : "❌ NO");
      }
      if (!status.cloudflare.present && import.meta.env.DEV) {
        console.log("   Solution: Configure domain in Cloudflare and update DNS");
      } else if (!status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("   Solution: Enable 'Add visitor location headers' in Cloudflare Managed Transforms");
      } else if (status.cloudflare.present && status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("✅ SUCCESS: Cloudflare and geolocation are properly configured!");
      }
    } catch (error) {
    }
  };

  // Reset Cloudflare availability status
  (window as any).resetCloudflareStatus = () => {
    (window as any).cloudflareUnavailable = false;
    if (import.meta.env.DEV) {
      console.log("🔄 Cloudflare availability status reset - will try again on next detection");
    }
  };

  // Force Cloudflare test (ignore unavailable flag)
  (window as any).forceCloudflareTest = async () => {
    const wasUnavailable = (window as any).cloudflareUnavailable;
    (window as any).cloudflareUnavailable = false;

    await (window as any).testCloudflareGeo();

    // Restore the flag
    (window as any).cloudflareUnavailable = wasUnavailable;
  };

  // Test external geolocation API (EMERGENCY BACKUP ONLY)
  (window as any).testExternalGeo = async () => {
    if (import.meta.env.DEV) {
      console.log(`🚨 Testing EXTERNAL geolocation API (should NOT be primary for ${BRAND_SITE_HOST})...`);
      console.log("   ⚠️  This uses paid/limited APIs - Cloudflare Enterprise should handle this");
    }
    try {
      const response = await fetch("/api/geolocate");
      const geoData = await response.json();
      if (geoData.country) {
        if (import.meta.env.DEV) console.log("   ⚠️  WARNING: Using external API instead of Cloudflare Enterprise");
        const { getTemplateForCountry } = await import("@/config/countryTemplates");
        getTemplateForCountry(geoData.country);
      } else if (import.meta.env.DEV) {
        console.log("   💡 This means NO geolocation is working - check all setups");
      }
    } catch (error) {
    }
  };

  // Quick Cloudflare diagnosis
  (window as any).diagnoseCloudflare = async () => {
    try {
      const statusResponse = await fetch("/api/cloudflare-status");
      const status = await statusResponse.json();
      if (import.meta.env.DEV) {
        console.log("   Result:", status.cloudflare.present ? "✅ CLOUDFLARE ACTIVE" : "❌ NO CLOUDFLARE");
        console.log("   CF-RAY present:", status.cloudflare.hasCfRay ? "✅ YES" : "❌ NO");
        console.log("   True-Client-IP:", status.cloudflare.hasTrueClientIp ? "✅ YES" : "❌ NO");
      }
      if (!status.cloudflare.present && import.meta.env.DEV) {
        console.log("   💡 SOLUTION: Check DNS nameservers point to Cloudflare");
        console.log("   🔗 HOW TO: CLOUDFLARE_DIAGNOSIS.md section 'Agregar Dominio a Cloudflare'");
      } else if (status.cloudflare.present && !status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("   ⚠️  WARNING: Cloudflare active but no geolocation headers!");
        console.log("   💡 SOLUTION: Enable 'Add visitor location headers' in Managed Transforms");
        console.log("   🔗 HOW TO: CLOUDFLARE_DIAGNOSIS.md section 'Configuración de Zone Settings'");
      }
    } catch (error) {
    }
    try {
      const testResponse = await fetch(window.location.origin, { method: "HEAD" });
      const cfRay = testResponse.headers.get("cf-ray");
      if (import.meta.env.DEV && cfRay) console.log("   CF-RAY:", cfRay);
    } catch (error) {
    }
    // Geolocation now handled by server via headers
    if (import.meta.env.DEV) {
      console.log("🎯 If CF-RAY exists but no country: Enable 'Add visitor location headers'");
    }
  };

  // Safe PostHog debug helpers
  (window as any).posthogDebug = () => {
    try {
      const posthogKey = import.meta.env.NEXT_PUBLIC_POSTHOG_KEY || import.meta.env.VITE_POSTHOG_KEY || "";
      const posthogHost =
        import.meta.env.NEXT_PUBLIC_POSTHOG_HOST ||
        import.meta.env.VITE_POSTHOG_HOST ||
        (import.meta.env.DEV ? "/ingest" : "https://us.i.posthog.com");
      if (import.meta.env.DEV) {
        console.log("PostHog key:", import.meta.env.NEXT_PUBLIC_POSTHOG_KEY ? "NEXT_PUBLIC_POSTHOG_KEY" : import.meta.env.VITE_POSTHOG_KEY ? "VITE_POSTHOG_KEY" : "Not found");
        console.log("PostHog host:", import.meta.env.NEXT_PUBLIC_POSTHOG_HOST ? "NEXT_PUBLIC_POSTHOG_HOST" : import.meta.env.VITE_POSTHOG_HOST ? "VITE_POSTHOG_HOST" : "Default");
      }
      const posthog = (window as any).posthog;
      if (posthog && import.meta.env.DEV) {
        try {
          console.log("PostHog distinctId:", posthog.get_distinct_id?.());
        } catch (error) {
        }
      }
    } catch (error) {
    }
  };

  (window as any).posthogStartRecording = () => {
    try {
      const posthog = (window as any).posthog;
      if (posthog?.startSessionRecording) {
        posthog.startSessionRecording(true);
      } else {
      }
    } catch (error) {
    }
  };

  (window as any).posthogStopRecording = () => {
    try {
      const posthog = (window as any).posthog;
      if (posthog?.stopSessionRecording) {
        posthog.stopSessionRecording();
      } else {
      }
    } catch (error) {
    }
  };

  (window as any).posthogTestEvent = () => {
    try {
      if ((window as any).posthogSafeCapture) {
        (window as any).posthogSafeCapture("test_event", {
          source: "console_command",
          timestamp: new Date().toISOString(),
          user_region: getUserRegionForTracking(),
        });
      } else {
      }
    } catch (error) {
    }
  };

  if (import.meta.env.DEV) {
    console.log("%c📍 Region & Country Helpers Available!", "color: #667eea; font-size: 14px; font-weight: bold");
    console.log("🌍 Regions: setRegion('US') | setRegion('SPAIN') | setRegion('LATAM')");
    console.log("🌎 LATAM Countries: testCountry('MX') | testCountry('CL') | testCountry('PY') | testCountry('BR')");
    console.log(`🚀 Cloudflare Enterprise: testCloudflareGeo() - PRIMARY geolocation (${BRAND_SITE_HOST})`);
    console.log("🔍 Cloudflare: checkCloudflareStatus() - Check Cloudflare Enterprise setup");
    console.log("🔄 Cloudflare: resetCloudflareStatus() - Reset Cloudflare availability");
    console.log("🔧 Cloudflare: forceCloudflareTest() - Force test (debug only)");
    console.log("🚨 External: testExternalGeo() - EMERGENCY backup (paid/limited APIs)");
    console.log("📊 Templates: Worker home = BaseTemplate (todos los países; ver worker.js COUNTRY_TEMPLATE_MAP)");
    console.log("🎯 PostHog: posthogDebug() | posthogStartRecording() | posthogStopRecording() | posthogTestEvent()");
    console.log("📊 PostHog Safe Capture: posthogSafeCapture('event_name', { property: 'value' }) - Safe way to capture events");
    console.log("%c📈 Meta Pixel Monitor", "color: #1877f2; font-size: 13px; font-weight: bold");
    console.log("   __metaPixelStatus__()  → diagnóstico instantáneo (loaded, pixelId, events fired)");
    console.log("   __metaPixelLog__       → últimos 50 events disparados (PageView, ViewContent, etc.)");
    console.log("Example: testCountry('MX') - Tests Mexico (BaseTemplate + idioma es-MX)");
    console.log("Example: posthogDebug() - Shows PostHog status and debug info");
  }
}

// Render function - ensures DOM is ready
const renderApp = () => {

  try {
    const rootElement = document.getElementById("root");
    if (!rootElement) {
      console.error("❌ Root element not found - cannot render React application");
      document.body.innerHTML =
        '<div style="padding: 20px; color: red;"><h1>Root element not found</h1></div>';
      return;
    }


    const root = createRoot(rootElement);

    root.render(<App />);
  } catch (error) {

    // Try to show a basic error message
    try {
      const rootElement = document.getElementById("root");
      if (rootElement) {
        rootElement.innerHTML = `
          <div style="padding: 20px; font-family: Arial, sans-serif; color: red; border: 2px solid red;">
            <h1>🚨 Error Loading Application</h1>
            <p><strong>Error:</strong> ${error?.message || "Unknown error"}</p>
            <p><strong>Type:</strong> ${error?.name || "Error"}</p>
            <p>Please check the browser console for more details.</p>
            <button onclick="window.location.reload()" style="padding: 10px; background: red; color: white; border: none; border-radius: 5px; cursor: pointer;">Refresh Page</button>
          </div>
        `;
      } else {
        document.body.innerHTML =
          '<div style="padding: 20px; color: red;"><h1>Critical error and root element not found</h1></div>';
      }
    } catch (fallbackError) {
    }
  }
};

// Wait for DOM to be ready — salvo que estemos redirigiendo www→apex, en cuyo
// caso NO montamos el SPA. Montar en www dispararía fetch autenticados desde un
// host que el browser luego 308-redirige al apex perdiendo la cabecera
// Authorization (la causa raíz del "[clerkAuth] Missing Authorization header").
if (!isRedirectingToApex) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", renderApp);
  } else {
    // DOM is already ready, render immediately
    renderApp();
  }
}
